import { createContext, useContext, useMemo } from "react";
import useAppConfig from "../hooks/useAppConfig";
import { formatCurrency as formatCurrencyValue } from "../utils/currency";

const DEFAULTS = {
  appName: "Pulo",
  primaryCurrency: "NGN - Naira",
  systemTimezone: "(GMT +1) West Africa",
  logoUrl: "",
  supportEmail: "support@pulo.com",
  maintenanceMode: false,
  services: { rides: true, stays: true, puloAssistants: true },
};

const AppConfigContext = createContext({
  config: DEFAULTS,
  isLoading: false,
  formatCurrency: (amount) => formatCurrencyValue(amount, DEFAULTS.primaryCurrency),
  isServiceEnabled: () => true,
});

/**
 * Wraps the app so every screen can read the live platform configuration
 * (branding, currency, timezone, support email, service module toggles) set
 * on the admin Settings page, instead of each component hardcoding its own
 * copy. Falls back to sane defaults while loading or if the request fails,
 * so a settings-endpoint hiccup never blocks the rest of the app.
 */
export function AppConfigProvider({ children }) {
  const { data, isLoading } = useAppConfig();
  const config = data ? { ...DEFAULTS, ...data, services: { ...DEFAULTS.services, ...data.services } } : DEFAULTS;

  const value = useMemo(
    () => ({
      config,
      isLoading,
      formatCurrency: (amount, options) =>
        formatCurrencyValue(amount, config.primaryCurrency, options),
      isServiceEnabled: (service) => config.services?.[service] ?? true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config), isLoading]
  );

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export const useAppConfigContext = () => useContext(AppConfigContext);

export default AppConfigContext;
